<script setup>
/* eslint-disable vue/no-unused-properties, no-unused-vars, no-use-before-define, vue/custom-event-name-casing, no-console, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, watch, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PdvItemPriceControl from './PdvItemPriceControl.vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import IntegrahubSalesAPI from 'dashboard/api/integrahubSales';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  cart: {
    type: Array,
    required: true,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
  contactId: {
    type: [String, Number],
    required: true,
  },
  allowProductDiscountPerItem: {
    type: Boolean,
    default: false,
  },
  maxDiscountPercent: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'update-quantity',
  'update-item-price',
  'remove-from-cart',
  'clear-cart',
]);

const store = useStore();
const currentAccountId = useMapGetter('getCurrentAccountId');
const isProcessing = ref(false);
const isSendingQuote = ref(false);
const paymentMethod = ref('money');
const discount = ref(0);
const notes = ref('');
const customerName = ref('');
const customerCpf = ref('');
const saleCode = ref(String(props.conversationId || ''));
const saleDate = ref('');
const deliveryDate = ref('');
const deliveryContactName = ref('');
const deliveryChangeFor = ref(0);
const deliveryFee = ref(0);
const deliveryPhone = ref('');
const deliveryMobile = ref('');
const deliveryAllowWhatsApp = ref(false);
const deliveryAddressLine = ref('');
const deliveryAddressNumber = ref('');
const deliveryAddressComplement = ref('');
const deliveryAddressZip = ref('');
const deliveryAddressReference = ref('');
const deliveryAddressNeighborhood = ref('');
const deliveryAddressCity = ref('');
const deliveryAddressState = ref('');
const deliveryAddressRecipient = ref('');
const paymentNsu = ref('');
const paymentCardBrand = ref('');
const paymentDueDate = ref('');
const hasDelivery = ref(true);
const paymentDropdownOpen = ref(false);
const existingSales = ref([]);
const allowResubmit = ref(false);

const hasSentSale = computed(
  () => existingSales.value.length > 0 && !allowResubmit.value
);

const latestSale = computed(() => existingSales.value[0] || null);

const formatDate = isoString => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('pt-BR');
};

const closePaymentDropdown = () => {
  paymentDropdownOpen.value = false;
};

const currentAccount = computed(() => {
  const accountFromRecords = store.getters['accounts/getAccount']?.(
    currentAccountId.value
  );
  return accountFromRecords || store.getters.getCurrentAccount || {};
});
const currentContact = computed(
  () => store.getters['contacts/getContact'](props.contactId) || {}
);
const currentContactAddress = computed(() => {
  const attributes = currentContact.value?.additional_attributes || {};
  const address = attributes.address || {};
  return {
    // Prefer standard flat contact fields and keep nested `address` as legacy fallback.
    line: attributes.address_line || address.line || '',
    number: attributes.address_number || address.number || '',
    complement: attributes.address_complement || address.complement || '',
    neighborhood: attributes.address_neighborhood || address.neighborhood || '',
    zip: attributes.address_zip || address.zip || '',
    state: attributes.address_state || address.state || '',
    reference: attributes.address_reference || address.reference || '',
    recipient: attributes.address_recipient || address.recipient || '',
  };
});
const requiredAddressFields = computed(() => ({
  line: deliveryAddressLine.value,
  number: deliveryAddressNumber.value,
  zip: deliveryAddressZip.value,
  neighborhood: deliveryAddressNeighborhood.value,
  city: deliveryAddressCity.value,
  state: deliveryAddressState.value,
  recipient: deliveryAddressRecipient.value,
}));
const isAddressComplete = computed(() =>
  Object.values(requiredAddressFields.value).every(
    value => String(value || '').trim() !== ''
  )
);
const missingAddressFields = computed(() => {
  const labels = {
    line: 'Endereço',
    number: 'Número',
    zip: 'CEP',
    neighborhood: 'Bairro',
    city: 'Cidade',
    state: 'Estado',
    recipient: 'Destinatário',
  };

  return Object.entries(requiredAddressFields.value)
    .filter(([, value]) => String(value || '').trim() === '')
    .map(([key]) => labels[key]);
});

const toDatetimeLocalValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoString = value => {
  if (!value) return new Date().toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? new Date().toISOString()
    : parsed.toISOString();
};

const paymentTypeByMethod = method => {
  const mapping = {
    money: 'D',
    credit: 'C',
    debit: 'D',
    pix: 'W',
  };
  return mapping[method] || 'D';
};

const paymentFormLabel = computed(() => {
  const labels = {
    money: 'Dinheiro',
    credit: 'Cartão Crédito',
    debit: 'Cartão Débito',
    pix: 'PIX',
  };
  return labels[paymentMethod.value] || 'Dinheiro';
});

watch(
  currentContact,
  contact => {
    if (!customerName.value) customerName.value = contact?.name || '';
    if (!deliveryContactName.value) {
      deliveryContactName.value = contact?.name || '';
    }
    if (!deliveryPhone.value) {
      deliveryPhone.value = contact?.phone_number || '';
    }
    if (!deliveryMobile.value) {
      deliveryMobile.value = contact?.phone_number || '';
    }
    if (!deliveryAddressLine.value) {
      deliveryAddressLine.value = currentContactAddress.value?.line || '';
    }
    if (!deliveryAddressNumber.value) {
      deliveryAddressNumber.value = currentContactAddress.value?.number || '';
    }
    if (!deliveryAddressComplement.value) {
      deliveryAddressComplement.value =
        currentContactAddress.value?.complement || '';
    }
    if (!deliveryAddressNeighborhood.value) {
      deliveryAddressNeighborhood.value =
        currentContactAddress.value?.neighborhood || '';
    }
    if (!deliveryAddressZip.value) {
      deliveryAddressZip.value = currentContactAddress.value?.zip || '';
    }
    if (!deliveryAddressState.value) {
      deliveryAddressState.value = currentContactAddress.value?.state || '';
    }
    if (!deliveryAddressReference.value) {
      deliveryAddressReference.value =
        currentContactAddress.value?.reference || '';
    }
    if (!deliveryAddressRecipient.value) {
      deliveryAddressRecipient.value =
        currentContactAddress.value?.recipient || contact?.name || '';
    }
    if (!customerCpf.value) {
      customerCpf.value = contact?.additional_attributes?.cpf || '';
    }
    if (!deliveryAddressCity.value) {
      deliveryAddressCity.value = contact?.additional_attributes?.city || '';
    }
    if (
      !saleCode.value ||
      saleCode.value === String(props.conversationId || '')
    ) {
      const contactId = contact?.id || props.contactId || 'contact';
      const conversationId = props.conversationId || 'conv';
      saleCode.value = `GC-${contactId}-${conversationId}-${Date.now()}`;
    }
  },
  { immediate: true }
);

const setDefaultDates = () => {
  const now = new Date();
  const localValue = toDatetimeLocalValue(now);
  if (!deliveryDate.value) deliveryDate.value = localValue;
  if (!paymentDueDate.value) paymentDueDate.value = localValue;
};

setDefaultDates();

onMounted(async () => {
  if (!saleCode.value) return;
  try {
    const { data } = await IntegrahubSalesAPI.index({
      saleCode: saleCode.value,
    });
    existingSales.value = Array.isArray(data) ? data : [];
  } catch {
    // silent — if the check fails, just let the operator proceed normally
  }
});

const subtotal = computed(() => {
  return props.cart.reduce((total, item) => {
    const itemPrice = item.customPrice ?? item.price;
    return total + itemPrice * item.quantity;
  }, 0);
});

const itemDiscountPercent = item => {
  const tablePrice = item.price;
  const selected = item.customPrice ?? tablePrice;
  if (!tablePrice || tablePrice === selected) return 0;

  const exactPercent = ((tablePrice - selected) / tablePrice) * 100;

  for (let decimals = 0; decimals <= 2; decimals += 1) {
    const factor = 10 ** decimals;
    const roundedPercent = Math.round(exactPercent * factor) / factor;
    const testPrice =
      Math.round(tablePrice * (1 - roundedPercent / 100) * 100) / 100;
    if (testPrice === selected) {
      return roundedPercent;
    }
  }

  return Math.round(exactPercent * 100) / 100;
};

// A price picked from the item's own IntegraHub price table (Tabela/Promoção)
// isn't an agent-applied discount, so it shouldn't count against the account's
// max_discount_percent — that cap only limits discounts the agent adds by hand.
const isCatalogPricedItem = item => {
  const selected = item.customPrice ?? item.price;
  return (item.availablePrices || []).some(opt => opt.price === selected);
};

// A product's own catalog MAXIMUMDISCOUNT, when non-zero, takes priority over
// the account's max_discount_percent as that item's discount cap.
const itemMaxDiscountPercent = item => {
  if (item.maxDiscountPercent > 0) return item.maxDiscountPercent;
  return props.maxDiscountPercent;
};

const hasDiscountCap = computed(
  () =>
    props.maxDiscountPercent != null ||
    props.cart.some(item => item.maxDiscountPercent > 0)
);

// The general discount combines (additively) with a product's own discount,
// so it can't push any item's combined discount past that item's own cap.
// This mirrors the backend's Pdv::CombinedDiscountClamp: the room left for
// the general discount is the smallest (item cap - item discount) in the cart.
// A catalog-priced item still counts its own cap (just with a 0% item
// discount, since the catalog price itself isn't a manual discount) — it
// must not be dropped from the room calculation entirely, or an untouched
// item (which is always "catalog priced" by default) leaves no cap at all.
const maxGeneralDiscountAllowed = computed(() => {
  const rooms = props.cart.map(item => {
    const cap = itemMaxDiscountPercent(item);
    if (cap == null) return Infinity;
    const discountPercent = isCatalogPricedItem(item)
      ? 0
      : itemDiscountPercent(item);
    return cap - discountPercent;
  });
  const minRoom = rooms.length ? Math.min(...rooms) : Infinity;
  return minRoom === Infinity ? 100 : Math.max(0, minRoom);
});

const effectiveDiscount = computed(() => {
  if (hasDiscountCap.value) {
    return Math.min(discount.value, maxGeneralDiscountAllowed.value);
  }
  return discount.value;
});

// Re-clamp the general discount whenever it's edited directly, or whenever
// an item discount changes and would push the combined total above the
// account's limit.
watch([discount, maxGeneralDiscountAllowed], ([value, allowed]) => {
  if (value > allowed) discount.value = allowed;
});

const discountAmount = computed(() => {
  return (subtotal.value * effectiveDiscount.value) / 100;
});

const total = computed(() => {
  return subtotal.value - discountAmount.value;
});

const formatPrice = price => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
};

const updateItemQuantity = (productId, newQuantity) => {
  emit('update-quantity', productId, newQuantity);
};

const applyItemDiscountPercent = (item, percent) => {
  const itemCap = itemMaxDiscountPercent(item);
  const upperBound =
    itemCap != null
      ? Math.min(99.99, Math.max(0, itemCap - discount.value))
      : 99.99;
  const clamped = Math.min(upperBound, Math.max(0, percent));
  const newPrice = Math.round(item.price * (1 - clamped / 100) * 100) / 100;
  emit('update-item-price', item.id, newPrice);
};

const handleItemDiscountInput = (item, value) => {
  const parsed = parseFloat(String(value).replace(',', '.'));
  if (!Number.isNaN(parsed)) {
    applyItemDiscountPercent(item, parsed);
  }
};

const incrementItemDiscount = item => {
  applyItemDiscountPercent(item, itemDiscountPercent(item) + 1);
};

const decrementItemDiscount = item => {
  applyItemDiscountPercent(item, itemDiscountPercent(item) - 1);
};

const removeItem = productId => {
  emit('remove-from-cart', productId);
};

const clearCart = () => {
  emit('clear-cart');
};

const sendQuote = async () => {
  if (props.cart.length === 0) {
    useAlert('Carrinho vazio! Adicione produtos antes de enviar o orçamento.');
    return;
  }

  isSendingQuote.value = true;

  try {
    // Calculate total original price (before any discounts)
    const totalOriginalPrice = props.cart.reduce((sum, item) => {
      return sum + item.price * item.quantity;
    }, 0);

    // Calculate total savings
    const totalSavings = totalOriginalPrice - total.value;

    // Format quote message
    let quoteMessage = '';

    // List products
    props.cart.forEach(item => {
      const itemPrice = item.customPrice ?? item.price;
      const originalItemTotal = item.price * item.quantity;
      const finalItemTotal = itemPrice * item.quantity;

      quoteMessage += `${item.quantity} X ${item.name}\n`;

      if (originalItemTotal !== finalItemTotal) {
        quoteMessage += `De: ${formatPrice(originalItemTotal)}   Por: ${formatPrice(finalItemTotal)}\n`;
      } else {
        quoteMessage += `Por: ${formatPrice(finalItemTotal)}\n`;
      }

      quoteMessage += '\n';
    });

    // Separator
    quoteMessage += '_____________________________________________\n';

    // Total section
    quoteMessage += '*Total:*\n';

    // Show "De:" and "Por:" for total if there's any savings
    if (totalSavings > 0) {
      quoteMessage += `De: ${formatPrice(totalOriginalPrice)}   Por: ${formatPrice(total.value)}\n`;
      quoteMessage += `Você economizou: ${formatPrice(totalSavings)}\n`;
    } else {
      quoteMessage += `${formatPrice(total.value)}\n`;
    }

    quoteMessage += '_____________________________________________\n';

    // Notes
    if (notes.value) {
      quoteMessage += `\n*Observações:*\n${notes.value}\n`;
    }

    // Send message to the draft editor
    const draftKey = `draft-${props.conversationId}-REPLY`;

    // Save to store
    await store.dispatch('draftMessages/set', {
      key: draftKey,
      message: quoteMessage,
    });

    // Force ReplyBox to reload the draft using a custom event
    emitter.emit('load-draft-message', {
      conversationId: props.conversationId,
      replyType: 'REPLY',
    });

    useAlert('Orçamento adicionado ao campo de mensagem. Revise e envie.');
  } catch (error) {
    console.error('Erro ao preparar orçamento:', error);
    useAlert('Erro ao preparar orçamento. Tente novamente.');
  } finally {
    isSendingQuote.value = false;
  }
};

const finalizeSale = async () => {
  if (props.cart.length === 0) {
    useAlert('Carrinho vazio!');
    return;
  }

  if (!currentAccount.value?.cnpj) {
    if (currentAccountId.value) {
      await store.dispatch('accounts/get');
    }
    if (!currentAccount.value?.cnpj) {
      useAlert(
        'CNPJ da conta não encontrado. Atualize o CNPJ para finalizar a venda.'
      );
      return;
    }
  }

  if (
    hasDiscountCap.value &&
    discount.value > maxGeneralDiscountAllowed.value
  ) {
    useAlert(
      `Desconto máximo permitido é de ${maxGeneralDiscountAllowed.value}%. Ajuste o desconto para continuar.`
    );
    return;
  }

  if (!customerName.value?.trim()) {
    useAlert('Nome do cliente é obrigatório.');
    return;
  }

  if (hasDelivery.value && !isAddressComplete.value) {
    const missing = missingAddressFields.value.join(', ');
    useAlert(
      `Campos obrigatórios do endereço faltando: ${missing || 'verifique os dados'}.`
    );
    return;
  }

  isProcessing.value = true;

  try {
    // Aqui você fará a integração com seu ERP
    saleDate.value = toDatetimeLocalValue(new Date());

    const currentAgent = store.getters.getCurrentUser;
    const salePayload = {
      cnpjEmpresa: currentAccount.value?.cnpj || '',
      codigoVendaOnLine: saleCode.value || String(props.conversationId || ''),
      dataVenda: toIsoString(saleDate.value),
      nomeCliente: customerName.value || currentContact.value?.name || '',
      cpfCliente: (
        customerCpf.value ||
        currentContact.value?.additional_attributes?.cpf ||
        ''
      ).replace(/\D/g, ''),
      tipoDeEcommerce: 'GENIUS CLOUD',
      vendedor: {
        id: currentAgent?.inovafarma_id || '',
        nome: currentAgent?.name || '',
        email: currentAgent?.email || '',
        cpf: (currentAgent?.cpf || '').replace(/\D/g, ''),
      },
      entrega: hasDelivery.value
        ? {
            dataAgendamento: toIsoString(deliveryDate.value),
            contato:
              deliveryContactName.value || customerName.value || 'CONSUMIDOR',
            trocoPara: Number(deliveryChangeFor.value) || 0,
            taxa: Number(deliveryFee.value) || 0,
            telefone: deliveryPhone.value || '',
            celular: deliveryMobile.value || '',
            permitirEnvioMensagemWhatsApp: false,
            endereco: {
              endereco: deliveryAddressLine.value || '',
              numero: deliveryAddressNumber.value || '',
              complemento: deliveryAddressComplement.value || '',
              cep: deliveryAddressZip.value || '',
              referencia: deliveryAddressReference.value || '',
              bairro: deliveryAddressNeighborhood.value || '',
              cidade: deliveryAddressCity.value || '',
              estado: deliveryAddressState.value || '',
              destinatario: deliveryAddressRecipient.value || '',
            },
          }
        : undefined,
      produtos: props.cart.map(item => ({
        codigoProduto: item.sku || item.id,
        ean: item.ean || '',
        nomeProduto: item.name,
        imagemProduto: item.image || '',
        quantidade: item.quantity,
        unidade: item.unit || 'un',
        valorUnitario: Number(
          props.allowProductDiscountPerItem || isCatalogPricedItem(item)
            ? (item.customPrice ?? item.price)
            : item.price
        ),
        descontoUnitario: props.allowProductDiscountPerItem
          ? Math.max(
              0,
              Number(item.price ?? 0) -
                Number(item.customPrice ?? item.price ?? 0)
            )
          : 0,
      })),
      pagamentos: [
        {
          tipo: paymentTypeByMethod(paymentMethod.value),
          nsu: paymentNsu.value || '',
          bandeiraCartao:
            paymentMethod.value === 'pix'
              ? 'PIX'
              : paymentCardBrand.value || '',
          receberNaEntrega: hasDelivery.value,
          formaPagamento: paymentFormLabel.value,
          valor: Number(total.value || 0),
          dataVencimento: toIsoString(paymentDueDate.value),
        },
      ],
    };
    await IntegrahubSalesAPI.create({
      payload: salePayload,
      conversationId: props.conversationId,
      contactId: props.contactId,
    });

    useAlert('Venda finalizada com sucesso!');

    existingSales.value = [
      {
        sale_code: salePayload.codigoVendaOnLine,
        sale_date: salePayload.dataVenda,
        total_amount: total.value,
        created_at: new Date().toISOString(),
      },
      ...existingSales.value,
    ];
    allowResubmit.value = false;

    // Persist CPF and address to the contact (only fills empty fields, never overwrites)
    const existingAttrs = currentContact.value?.additional_attributes || {};
    const flatUpdates = {};

    if (!existingAttrs.address_line && deliveryAddressLine.value)
      flatUpdates.address_line = deliveryAddressLine.value;
    if (!existingAttrs.address_number && deliveryAddressNumber.value)
      flatUpdates.address_number = deliveryAddressNumber.value;
    if (!existingAttrs.address_complement && deliveryAddressComplement.value)
      flatUpdates.address_complement = deliveryAddressComplement.value;
    if (
      !existingAttrs.address_neighborhood &&
      deliveryAddressNeighborhood.value
    )
      flatUpdates.address_neighborhood = deliveryAddressNeighborhood.value;
    if (!existingAttrs.address_zip && deliveryAddressZip.value)
      flatUpdates.address_zip = deliveryAddressZip.value;
    if (!existingAttrs.address_state && deliveryAddressState.value)
      flatUpdates.address_state = deliveryAddressState.value;
    if (!existingAttrs.address_reference && deliveryAddressReference.value)
      flatUpdates.address_reference = deliveryAddressReference.value;
    if (!existingAttrs.address_recipient && deliveryAddressRecipient.value)
      flatUpdates.address_recipient = deliveryAddressRecipient.value;
    if (!existingAttrs.cpf && customerCpf.value)
      flatUpdates.cpf = customerCpf.value;
    if (!existingAttrs.city && deliveryAddressCity.value)
      flatUpdates.city = deliveryAddressCity.value;

    const hasFlatUpdates = Object.keys(flatUpdates).length > 0;

    if (hasFlatUpdates) {
      const newAttrs = {
        ...existingAttrs,
        ...flatUpdates,
      };
      try {
        await store.dispatch('contacts/update', {
          id: props.contactId,
          additional_attributes: newAttrs,
        });
      } catch (error) {
        console.warn(
          'Erro ao salvar informações de endereço do contato:',
          error
        );
      }
    }

    clearCart();
    discount.value = 0;
    notes.value = '';
    paymentMethod.value = 'money';
    paymentNsu.value = '';
    paymentCardBrand.value = '';
    saleCode.value = `GC-${props.contactId || 'contact'}-${props.conversationId || 'conv'}-${Date.now()}`;
    customerName.value = currentContact.value?.name || '';
    customerCpf.value = currentContact.value?.additional_attributes?.cpf || '';
    deliveryContactName.value = currentContact.value?.name || '';
    deliveryPhone.value = currentContact.value?.phone_number || '';
    deliveryMobile.value = currentContact.value?.phone_number || '';
    deliveryAddressLine.value = currentContactAddress.value?.line || '';
    deliveryAddressNumber.value = currentContactAddress.value?.number || '';
    deliveryAddressComplement.value =
      currentContactAddress.value?.complement || '';
    deliveryAddressZip.value = currentContactAddress.value?.zip || '';
    deliveryAddressReference.value =
      currentContactAddress.value?.reference || '';
    deliveryAddressNeighborhood.value =
      currentContactAddress.value?.neighborhood || '';
    deliveryAddressCity.value =
      currentContact.value?.additional_attributes?.city || '';
    deliveryAddressState.value = currentContactAddress.value?.state || '';
    deliveryAddressRecipient.value =
      currentContactAddress.value?.recipient ||
      currentContact.value?.name ||
      '';
    deliveryChangeFor.value = 0;
    deliveryFee.value = 0;
    deliveryAllowWhatsApp.value = false;
    setDefaultDates();
  } catch (error) {
    console.error('Erro ao finalizar venda:', error);
    useAlert('Erro ao finalizar venda. Tente novamente.');
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      v-if="cart.length === 0"
      class="flex flex-col items-center justify-center h-full text-center py-8"
    >
      <span class="text-4xl mb-2">🛒</span>
      <p class="text-n-text-subtle">
        {{ $t('PDV.EMPTY_CART') }}
      </p>
    </div>

    <div v-else class="relative flex flex-col lg:flex-row h-full">
      <!-- Banner: venda já enviada -->
      <div
        v-if="hasSentSale"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-n-background/95 p-6 text-center gap-4"
      >
        <span class="i-ph-check-circle-bold text-5xl text-green-500" />
        <h3 class="text-lg font-bold text-n-text">Venda já enviada</h3>
        <p class="text-sm text-n-text-subtle">
          Esta conversa já possui uma venda registrada.
        </p>
        <div
          class="bg-n-solid-2 border border-n-weak rounded-lg p-4 text-left w-full max-w-xs space-y-1"
        >
          <p class="text-xs text-n-text-subtle">
            Código:
            <span class="font-mono text-n-text">{{
              latestSale.sale_code
            }}</span>
          </p>
          <p class="text-xs text-n-text-subtle">
            Enviada em:
            <span class="text-n-text">{{
              formatDate(latestSale.created_at)
            }}</span>
          </p>
          <p class="text-xs text-n-text-subtle">
            Total:
            <span class="font-semibold text-n-brand">{{
              formatPrice(latestSale.total_amount)
            }}</span>
          </p>
        </div>
        <Button ghost slate xs @click="allowResubmit = true">
          Enviar nova venda mesmo assim
        </Button>
      </div>

      <!-- Carrinho -->
      <div class="flex-1 min-w-0 overflow-y-auto p-4 space-y-3">
        <div class="space-y-3">
          <div
            v-for="item in cart"
            :key="item.id"
            class="bg-n-solid-1 rounded-lg p-3 border border-n-weak"
          >
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1 min-w-0">
                <h4
                  class="font-semibold text-n-text text-sm truncate"
                  :title="item.name"
                >
                  {{ item.name }}
                </h4>
                <p
                  v-if="item.brand"
                  class="text-xs text-n-text-subtle truncate"
                >
                  {{ item.brand }}
                </p>
                <p class="text-xs text-n-text-subtle">
                  Tabela: {{ formatPrice(item.price) }} / un
                </p>
              </div>
              <Button
                ghost
                danger
                xs
                icon="i-ph-trash-bold"
                @click="removeItem(item.id)"
              />
            </div>

            <!-- Price & Discount Controls -->
            <div class="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label class="text-xs text-n-text-subtle mb-1 block"
                  >Preço</label
                >
                <PdvItemPriceControl
                  :item="item"
                  :allow-edit="props.allowProductDiscountPerItem"
                  :max-discount-percent="itemMaxDiscountPercent(item)"
                  :general-discount="discount"
                  :discount-percent="itemDiscountPercent(item)"
                  @update-price="
                    (productId, price) =>
                      emit('update-item-price', productId, price)
                  "
                />
              </div>
              <div>
                <label class="text-xs text-n-text-subtle mb-1 block"
                  >Desconto</label
                >
                <span
                  v-if="!props.allowProductDiscountPerItem"
                  class="text-sm text-n-text-subtle"
                  >—</span
                >
                <div v-else class="flex items-center gap-1 -ml-1">
                  <Button
                    ghost
                    xs
                    icon="i-ph-minus-bold"
                    @click="decrementItemDiscount(item)"
                  />
                  <div
                    class="flex items-center justify-center w-16 border rounded overflow-hidden"
                    :class="
                      itemDiscountPercent(item) > 0
                        ? 'border-green-400'
                        : 'border-n-weak'
                    "
                  >
                    <input
                      type="text"
                      inputmode="decimal"
                      :value="itemDiscountPercent(item)"
                      class="reset-base w-full text-sm font-semibold text-right bg-transparent !border-none !shadow-none !outline-none py-0 pl-0 pr-0.5 m-0 h-7 focus:ring-0 min-w-0"
                      :class="
                        itemDiscountPercent(item) > 0
                          ? 'text-green-600'
                          : 'text-n-text'
                      "
                      @change="
                        e => handleItemDiscountInput(item, e.target.value)
                      "
                    />
                    <span
                      class="text-sm font-semibold pr-1.5"
                      :class="
                        itemDiscountPercent(item) > 0
                          ? 'text-green-600'
                          : 'text-n-text-subtle'
                      "
                      >%</span
                    >
                  </div>
                  <Button
                    ghost
                    xs
                    icon="i-ph-plus-bold"
                    @click="incrementItemDiscount(item)"
                  />
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <Button
                  ghost
                  xs
                  icon="i-ph-minus-bold"
                  @click="updateItemQuantity(item.id, item.quantity - 1)"
                />
                <span class="text-sm font-medium px-2">{{
                  item.quantity
                }}</span>
                <Button
                  ghost
                  xs
                  icon="i-ph-plus-bold"
                  @click="updateItemQuantity(item.id, item.quantity + 1)"
                />
              </div>
              <div class="text-right">
                <span
                  v-if="
                    item.customPrice != null && item.customPrice !== item.price
                  "
                  class="block text-xs text-n-text-subtle line-through"
                >
                  {{ formatPrice(item.price * item.quantity) }}
                </span>
                <span class="font-bold text-n-brand">
                  {{
                    formatPrice(
                      (item.customPrice ?? item.price) * item.quantity
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumo e Finalização -->
      <div
        class="w-full lg:w-[360px] xl:w-[420px] border-t border-n-weak lg:border-t-0 lg:border-l p-4 space-y-4 bg-n-solid-1 overflow-y-auto"
      >
        <!-- Dados da Venda -->
        <div class="space-y-2">
          <h4 class="text-sm font-semibold text-n-text">
            {{ $t('PDV.SALE_DETAILS') }}
          </h4>
          <div class="grid grid-cols-1 gap-2">
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.SALE_CODE') }}
              </label>
              <Input
                v-model="saleCode"
                :placeholder="$t('PDV.SALE_CODE_PLACEHOLDER')"
                disabled
              />
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.CUSTOMER_NAME') }}
              </label>
              <Input
                v-model="customerName"
                :placeholder="$t('PDV.CUSTOMER_NAME_PLACEHOLDER')"
              />
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.CUSTOMER_CPF') }}
              </label>
              <Input
                v-model="customerCpf"
                :placeholder="$t('PDV.CUSTOMER_CPF_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>

        <!-- Entrega -->
        <div class="space-y-2">
          <label
            class="flex items-center gap-2 text-sm font-semibold text-n-text"
          >
            <input
              v-model="hasDelivery"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak text-n-brand"
            />
            {{ $t('PDV.HAS_DELIVERY') }}
          </label>
          <div v-if="hasDelivery" class="grid grid-cols-1 gap-2">
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.DELIVERY_DATE') }}
              </label>
              <Input v-model="deliveryDate" type="datetime-local" />
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.DELIVERY_CONTACT') }}
              </label>
              <Input
                v-model="deliveryContactName"
                :placeholder="$t('PDV.DELIVERY_CONTACT_PLACEHOLDER')"
              />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.DELIVERY_CHANGE_FOR') }}
                </label>
                <Input
                  v-model.number="deliveryChangeFor"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.DELIVERY_FEE') }}
                </label>
                <Input
                  v-model.number="deliveryFee"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.DELIVERY_PHONE') }}
                </label>
                <Input
                  v-model="deliveryPhone"
                  :placeholder="$t('PDV.DELIVERY_PHONE_PLACEHOLDER')"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.DELIVERY_MOBILE') }}
                </label>
                <Input
                  v-model="deliveryMobile"
                  :placeholder="$t('PDV.DELIVERY_MOBILE_PLACEHOLDER')"
                />
              </div>
            </div>
            <label
              class="flex items-center gap-2 text-sm text-n-text opacity-60"
            >
              <input
                v-model="deliveryAllowWhatsApp"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-n-brand"
                disabled
              />
              {{ $t('PDV.DELIVERY_ALLOW_WHATSAPP') }}
            </label>
          </div>
        </div>

        <!-- Endereco -->
        <div v-if="hasDelivery" class="space-y-2">
          <h4 class="text-sm font-semibold text-n-text">
            {{ $t('PDV.DELIVERY_ADDRESS') }}
          </h4>
          <div class="grid grid-cols-1 gap-2">
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_LINE') }}
                </label>
                <Input
                  v-model="deliveryAddressLine"
                  :placeholder="$t('PDV.ADDRESS_LINE_PLACEHOLDER')"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_NUMBER') }}
                </label>
                <Input
                  v-model="deliveryAddressNumber"
                  :placeholder="$t('PDV.ADDRESS_NUMBER_PLACEHOLDER')"
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_COMPLEMENT') }}
                </label>
                <Input
                  v-model="deliveryAddressComplement"
                  :placeholder="$t('PDV.ADDRESS_COMPLEMENT_PLACEHOLDER')"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_ZIP') }}
                </label>
                <Input
                  v-model="deliveryAddressZip"
                  :placeholder="$t('PDV.ADDRESS_ZIP_PLACEHOLDER')"
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_REFERENCE') }}
                </label>
                <Input
                  v-model="deliveryAddressReference"
                  :placeholder="$t('PDV.ADDRESS_REFERENCE_PLACEHOLDER')"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_NEIGHBORHOOD') }}
                </label>
                <Input
                  v-model="deliveryAddressNeighborhood"
                  :placeholder="$t('PDV.ADDRESS_NEIGHBORHOOD_PLACEHOLDER')"
                />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_CITY') }}
                </label>
                <Input
                  v-model="deliveryAddressCity"
                  :placeholder="$t('PDV.ADDRESS_CITY_PLACEHOLDER')"
                />
              </div>
              <div class="space-y-2">
                <label class="text-sm font-medium text-n-text">
                  {{ $t('PDV.ADDRESS_STATE') }}
                </label>
                <Input
                  v-model="deliveryAddressState"
                  :placeholder="$t('PDV.ADDRESS_STATE_PLACEHOLDER')"
                />
              </div>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium text-n-text">
                {{ $t('PDV.ADDRESS_RECIPIENT') }}
              </label>
              <Input
                v-model="deliveryAddressRecipient"
                :placeholder="$t('PDV.ADDRESS_RECIPIENT_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>

        <!-- Desconto -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.DISCOUNT') }} (%)
          </label>
          <Input
            v-model.number="discount"
            type="number"
            min="0"
            :max="hasDiscountCap ? maxGeneralDiscountAllowed : 100"
            :placeholder="$t('PDV.DISCOUNT_PLACEHOLDER')"
          />
          <p
            v-if="hasDiscountCap && discount > maxGeneralDiscountAllowed"
            class="text-xs text-ruby-600"
          >
            {{
              $t('PDV.DISCOUNT_LIMIT_EXCEEDED', {
                max: maxGeneralDiscountAllowed,
              })
            }}
          </p>
          <p v-else-if="hasDiscountCap" class="text-xs text-n-text-subtle">
            {{
              $t('PDV.DISCOUNT_LIMIT_HINT', { max: maxGeneralDiscountAllowed })
            }}
          </p>
        </div>

        <!-- Forma de Pagamento -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.PAYMENT_METHOD') }}
          </label>
          <div v-on-clickaway="closePaymentDropdown">
            <!-- Trigger -->
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 rounded border border-n-weak bg-n-background text-n-text text-left"
              @click="paymentDropdownOpen = !paymentDropdownOpen"
            >
              <span>{{
                {
                  money: 'Dinheiro',
                  credit: 'Cartão Crédito',
                  debit: 'Cartão Débito',
                  pix: 'PIX',
                }[paymentMethod]
              }}</span>
              <span
                class="i-lucide-chevron-down ml-1 shrink-0 transition-transform"
                :class="{ 'rotate-180': paymentDropdownOpen }"
              />
            </button>
            <!-- Options list -->
            <div
              v-if="paymentDropdownOpen"
              class="mt-1 rounded border border-n-weak bg-n-solid-2"
            >
              <button
                v-for="opt in [
                  { label: 'Dinheiro', value: 'money' },
                  { label: 'Cartão Crédito', value: 'credit' },
                  { label: 'Cartão Débito', value: 'debit' },
                  { label: 'PIX', value: 'pix' },
                ]"
                :key="opt.value"
                type="button"
                class="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-n-slate-3"
                @click="
                  paymentMethod = opt.value;
                  closePaymentDropdown();
                "
              >
                <span>{{ opt.label }}</span>
                <span
                  v-if="opt.value === paymentMethod"
                  class="i-lucide-check ml-1 shrink-0 text-n-brand"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- Observações -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.NOTES') }}
          </label>
          <textarea
            v-model="notes"
            :placeholder="$t('PDV.NOTES_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded border border-n-weak bg-n-background text-n-text resize-none"
            rows="2"
          />
        </div>

        <!-- Totais -->
        <div class="space-y-2 pt-4 border-t border-n-weak">
          <div class="flex justify-between text-sm">
            <span class="text-n-text-subtle">{{ $t('PDV.SUBTOTAL') }}</span>
            <span class="text-n-text">{{ formatPrice(subtotal) }}</span>
          </div>
          <div
            v-if="effectiveDiscount > 0"
            class="flex justify-between text-sm"
          >
            <span class="text-n-text-subtle">
              {{ $t('PDV.DISCOUNT') }} ({{ effectiveDiscount }}%)
            </span>
            <span class="text-green-600">
              -{{ formatPrice(discountAmount) }}
            </span>
          </div>
          <div
            class="flex justify-between text-lg font-bold pt-2 border-t border-n-weak"
          >
            <span class="text-n-text">{{ $t('PDV.TOTAL') }}</span>
            <span class="text-n-brand">{{ formatPrice(total) }}</span>
          </div>
        </div>

        <!-- Botões de Ação -->
        <div class="space-y-2">
          <Button
            class="w-full"
            variant="outline"
            slate
            :loading="isSendingQuote"
            :disabled="isProcessing"
            @click="sendQuote"
          >
            <span class="i-ph-file-text-bold mr-2" />
            {{ $t('PDV.SEND_QUOTE') }}
          </Button>
          <Button
            class="w-full"
            :loading="isProcessing"
            :disabled="isSendingQuote"
            @click="finalizeSale"
          >
            <span class="i-ph-check-circle-bold mr-2" />
            {{ $t('PDV.FINALIZE_SALE') }}
          </Button>
          <Button
            class="w-full"
            ghost
            danger
            :disabled="isProcessing || isSendingQuote"
            @click="clearCart"
          >
            <span class="i-ph-trash-bold mr-2" />
            {{ $t('PDV.CLEAR_CART') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
